import { acceptHMRUpdate, defineStore } from 'pinia'
import LocationService from '@/services/location.service'
import { local, STORAGE_KEYS } from '@/utils/storage'

export const useLocationStore = defineStore({
  id: 'locationStore',
  state: () => ({
    userLocation: '',
    permissionState: local.get(STORAGE_KEYS.LOCATION_PERMISSION_STATE) || 'unknown',
    lastErrorCode: null,
    lastUpdatedAt: null
  }),
  setup () {},
  actions: {
    setPermissionState (state) {
      this.permissionState = state
      local.set(STORAGE_KEYS.LOCATION_PERMISSION_STATE, state)
    },
    setLocationError (errorCode = null, permissionState = 'unavailable') {
      this.lastErrorCode = errorCode
      this.setPermissionState(permissionState)
    },
    getUserLocation () {
      return LocationService.getUserLocation().then(
        response => {
          this.userLocation = response.data
          return response
        }
      )
    },
    async refreshPermissionState () {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        this.setPermissionState('unsupported')
        return 'unsupported'
      }

      if (!navigator.permissions?.query) {
        return this.permissionState
      }

      try {
        const status = await navigator.permissions.query({ name: 'geolocation' })
        const nextState = status?.state || 'unknown'
        this.setPermissionState(nextState)
        return nextState
      } catch {
        return this.permissionState
      }
    },
    setUserLocations (latitude, longitude) {
      this.userLocation = {
        mapPerson: this.userLocation.mapPerson,
        userLat: latitude,
        userLong: longitude
      }
      this.lastErrorCode = null
      this.lastUpdatedAt = Date.now()
      this.setPermissionState('granted')
      const formData = {
        latitude: latitude,
        longitude: longitude
      }
      return LocationService.setUserLocations(formData).then(
        response => {
          return response
        }
      )
    },
    async syncBrowserLocation ({ persistToServer = true } = {}) {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        this.setLocationError('unsupported', 'unsupported')
        throw new Error('unsupported')
      }

      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const latitude = position.coords.latitude
            const longitude = position.coords.longitude

            try {
              if (persistToServer) {
                await this.setUserLocations(latitude, longitude)
              } else {
                this.userLocation = {
                  mapPerson: this.userLocation.mapPerson,
                  userLat: latitude,
                  userLong: longitude
                }
                this.lastErrorCode = null
                this.lastUpdatedAt = Date.now()
                this.setPermissionState('granted')
              }

              resolve({ latitude, longitude })
            } catch (error) {
              this.setLocationError('save_failed', 'granted')
              reject(error)
            }
          },
          (error) => {
            const errorCode = typeof error?.code === 'number' ? error.code : null
            const permissionState = errorCode === 1 ? 'denied' : 'unavailable'
            this.setLocationError(errorCode, permissionState)
            reject(error)
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        )
      })
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLocationStore, import.meta.hot))
}
